import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './add-area-modal.module.scss';
import { VALIDATION_REGEX } from '../../../../../shared/components/validation.config';
import { FormElementData } from '../../../../../shared/interfaces/form-element-data.interface';
import { trackEvent } from '../../../../../shared/services/utils/tracking/tracking.service';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import {
  InputText,
  InputTextErrorMode,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import { FormControl, MenuItem, Select } from '@material-ui/core';

export interface AddAreaModalProps {
  data: {
    cites: {
      id: string;
      name: string;
    }[];
  };
  config: {
    showModal: boolean;
  };
  eventHandlers: {
    cancelHandler: () => void;
    addAreaHandler: (areaName: string, order: number, city: string, nameAr: string) => void;
  };
}

/**
 * functional component AddAreaModal
 * @param {AddAreaModalProps}
 */
export const AddAreaModal: React.FC<AddAreaModalProps> = ({ config, eventHandlers, data }) => {
  const [newAreaName, setNewAreaName] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });
  const [newAreaNameAr, setNewAreaNameAr] = useState<FormElementData<string>>({
    value: '',
    isValid: true
  });
  const [newAreaOrder, setNewAreaOrder] = useState<FormElementData<number>>({
    value: 0,
    isValid: false
  });
  const [cityName, setCityName] = useState<FormElementData<string | null>>({
    value: null,
    isValid: false
  });
  const { t } = useTranslation();
  const isMobileView = useWindowResizer();

  const handleNewAreaSubmit = () => {
    if (newAreaName.value !== '' && newAreaName.isValid && cityName.value && cityName.isValid) {
      eventHandlers.addAreaHandler(
        newAreaName.value,
        newAreaOrder.value,
        cityName.value,
        newAreaNameAr.value.trim()
      );
    }
  };

  return (
    <Modal
      config={{ showModal: config.showModal, fullScreen: false, showClose: true }}
      eventHandlers={{
        closeModalHandler: () => {
          eventHandlers.cancelHandler();
        }
      }}
    >
      <div className={styles['add-area-modal']}>
        <div className={styles['add-area-modal__title']}>
          {t('AREA_MODULE.LIST_CONTAINER.ADD_NEW_MODAL.TITLE')}
        </div>
        <div className={styles['add-area-modal__description']}>
          {t('AREA_MODULE.LIST_CONTAINER.ADD_NEW_MODAL.DESCRIPTION')}
        </div>
        <InputText
          data={{
            defaultValue: '',
            errorMessage: t('AREA_MODULE.LIST_CONTAINER.ADD_NEW_MODAL.INVALID_AREA_NAME'),
            placeholder: t('AREA_MODULE.LIST_CONTAINER.ADD_NEW_MODAL.NAME_PLACEHOLDER')
          }}
          config={{
            maxRows: 1,
            autoFocus: true,
            validationRegex: VALIDATION_REGEX.AREA_NAME,
            showErrorMode: InputTextErrorMode.instant
          }}
          eventHandlers={{
            onChangeHandler: (value: string, isValid: boolean) => {
              setNewAreaName({ value, isValid });
            },
            onEnterPressedHandler: handleNewAreaSubmit
          }}
        />
        <div className={styles['add-area-modal__description']}>
          {t('AREA_MODULE.LIST_CONTAINER.ADD_NEW_MODAL.NAME_AR_LABEL')}
        </div>
        <InputText
          data={{
            defaultValue: '',
            errorMessage: '',
            placeholder: t('AREA_MODULE.LIST_CONTAINER.ADD_NEW_MODAL.NAME_AR_PLACEHOLDER')
          }}
          config={{
            maxRows: 1,
            showErrorMode: InputTextErrorMode.instant
          }}
          eventHandlers={{
            onChangeHandler: (value: string) => {
              setNewAreaNameAr({ value, isValid: true, changed: true });
            },
            onEnterPressedHandler: handleNewAreaSubmit
          }}
        />
        <div className={styles['add-area-modal__description']}>Please select city </div>
        <FormControl
          variant="outlined"
          style={{
            minWidth: '120px'
          }}
        >
          <Select
            labelId="demo-simple-select-outlined-label"
            id="demo-simple-select-outlined"
            value={cityName.value}
            onChange={(
              event: React.ChangeEvent<{
                name?: string | undefined;
                value: unknown;
              }>
            ) => {
              setCityName({ value: event.target.value as string, isValid: true, changed: true });
            }}
          >
            {data.cites.map((city, index) => (
              <MenuItem key={index} value={city.id}>
                {city.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <div className={styles['add-area-modal__description']}>Order</div>
        <InputText
          data={{
            defaultValue: '',
            errorMessage: t('AREA_MODULE.LIST_CONTAINER.ADD_NEW_MODAL.INVALID_AREA_NAME'),
            placeholder: 'order'
          }}
          config={{
            maxRows: 1,
            showErrorMode: InputTextErrorMode.instant,
            type: InputTextType.number
          }}
          eventHandlers={{
            onChangeHandler: (value: string, isValid: boolean) => {
              setNewAreaOrder({ value: Number(value), isValid });
            },
            onEnterPressedHandler: handleNewAreaSubmit
          }}
        />
      </div>
      <div className={styles['add-area-modal--actions']}>
        <Button
          onClick={() => {
            eventHandlers.cancelHandler();
            trackEvent('record_edit_cancelled', { entity: 'area', method: 'cancel' });
          }}
          tracking={{ event: 'record_edit_cancelled', entity: 'builder', method: 'cancel' }}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          type={ButtonType.primaryInverted}
        >
          {t('AREA_MODULE.LIST_CONTAINER.ADD_NEW_MODAL.CANCEL_BUTTON')}
        </Button>
        <Button
          disabled={!newAreaName.isValid || newAreaName.value === ''}
          onClick={handleNewAreaSubmit}
          tracking={{ event: 'record_saved', entity: 'area', action: 'update', outcome: 'success' }}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
        >
          {t('AREA_MODULE.LIST_CONTAINER.ADD_NEW_MODAL.SUBMIT_BUTTON')}
        </Button>
      </div>
    </Modal>
  );
};
